<template>
  <div class="hs-pagination" v-if="totalItems > 0">
    <div class="hs-pagination__summary">
      <span class="hs-pagination__eyebrow">Showing</span>
      <strong>{{ startItem }}-{{ endItem }}</strong>
      <span>of {{ formattedTotalItems }}</span>
    </div>

    <div class="hs-pagination__per-page">
      <span>Rows</span>
      <select
        v-model="localItemsPerPage"
        @change="handlePerPageChange"
      >
        <option v-for="option in perPageOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
    </div>

    <div class="hs-pagination__controls">
      <button
        class="hs-pagination__btn"
        @click="$emit('previous-page')"
        :disabled="currentPage === 1"
        aria-label="Previous page"
      >
        <vue-feather type="chevron-left" size="16" />
        <span>Previous</span>
      </button>

      <div class="hs-pagination__page-pill">
        <span>Page</span>
        <strong>{{ currentPage }}</strong>
        <span>/ {{ safeTotalPages }}</span>
      </div>

      <button
        class="hs-pagination__btn hs-pagination__btn--primary"
        @click="$emit('next-page')"
        :disabled="currentPage === safeTotalPages"
        aria-label="Next page"
      >
        <span>Next</span>
        <vue-feather type="chevron-right" size="16" />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "HSPagination",
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      required: true,
      default: 10,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
  },
  data() {
    return {
      localItemsPerPage: this.getValidItemsPerPage(this.itemsPerPage),
    };
  },
  emits: ["update:itemsPerPage", "previous-page", "next-page"],
  computed: {
    safeTotalPages() {
      return Math.max(Number(this.totalPages || 1), 1);
    },
    startItem() {
      if (!this.totalItems) return 0;
      return (this.currentPage - 1) * this.localItemsPerPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.localItemsPerPage, this.totalItems);
    },
    formattedTotalItems() {
      return Number(this.totalItems || 0).toLocaleString();
    },
  },
  methods: {
    getValidItemsPerPage(value) {
      // If value is in options, use it; otherwise use first option
      const validValue = this.perPageOptions.includes(value)
        ? value
        : this.perPageOptions[0] || 10;
      return validValue;
    },
    handlePerPageChange() {
      this.$emit("update:itemsPerPage", this.localItemsPerPage);
    },
  },
  watch: {
    itemsPerPage: {
      handler(newValue) {
        if (newValue) {
          const validValue = this.getValidItemsPerPage(newValue);
          if (validValue !== this.localItemsPerPage) {
            this.localItemsPerPage = validValue;
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // Ensure localItemsPerPage is synced with prop on mount
    if (this.itemsPerPage) {
      const validValue = this.getValidItemsPerPage(this.itemsPerPage);
      if (validValue !== this.localItemsPerPage) {
        this.localItemsPerPage = validValue;
      }
    }
  },
};
</script>

<style scoped>
.hs-pagination {
  align-items: center;
  background:
    radial-gradient(circle at 0% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent), transparent 32%),
    color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 20px;
  box-shadow: var(--hs-shadow-card, 0 14px 38px rgba(15, 23, 42, 0.08));
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-top: 1rem;
  padding: 12px;
}

.hs-pagination__summary,
.hs-pagination__per-page,
.hs-pagination__controls,
.hs-pagination__page-pill,
.hs-pagination__btn {
  align-items: center;
  display: flex;
}

.hs-pagination__summary {
  color: var(--hs-muted, #64748b);
  gap: 7px;
  min-width: 0;
  white-space: nowrap;
}

.hs-pagination__summary strong {
  color: var(--hs-text, #111827);
  font-weight: 900;
}

.hs-pagination__eyebrow {
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.hs-pagination__per-page {
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 70%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.2));
  border-radius: 14px;
  color: var(--hs-muted, #64748b);
  gap: 8px;
  padding: 6px 8px 6px 12px;
}

.hs-pagination__per-page span {
  font-size: 0.76rem;
  font-weight: 900;
  text-transform: uppercase;
}

.hs-pagination__per-page select {
  background: transparent;
  border: 0;
  color: var(--hs-text, #111827);
  font-size: 0.86rem;
  font-weight: 900;
  outline: none;
}

.hs-pagination__controls {
  gap: 8px;
}

.hs-pagination__btn {
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 72%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.2));
  border-radius: 14px;
  color: var(--hs-text, #111827);
  font-size: 0.84rem;
  font-weight: 900;
  gap: 7px;
  min-height: 40px;
  padding: 0 13px;
  transition: box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.hs-pagination__btn--primary {
  background: linear-gradient(135deg, var(--hs-primary, #3293ec), #2563eb);
  border-color: transparent;
  color: white;
}

.hs-pagination__btn:not(:disabled):hover {
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  transform: translateY(-1px);
}

.hs-pagination__btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.hs-pagination__page-pill {
  background: #111827;
  border-radius: 14px;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.82rem;
  font-weight: 800;
  gap: 6px;
  min-height: 40px;
  padding: 0 14px;
}

.hs-pagination__page-pill strong {
  color: #facc15;
  font-size: 1rem;
  font-weight: 900;
}

.dark .hs-pagination {
  background:
    radial-gradient(circle at 0% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 16%, transparent), transparent 32%),
    color-mix(in srgb, var(--hs-surface, #111827) 92%, transparent);
}

@media (max-width: 760px) {
  .hs-pagination {
    align-items: stretch;
    flex-direction: column;
  }

  .hs-pagination__summary,
  .hs-pagination__per-page,
  .hs-pagination__controls {
    justify-content: space-between;
    width: 100%;
  }

  .hs-pagination__btn {
    flex: 1;
    justify-content: center;
  }
}

@media (max-width: 420px) {
  .hs-pagination__btn span {
    display: none;
  }
}
</style>
